/**
 * V&V Recommendations API
 *
 * Wraps `/api/v1/vv-recommendations/{threadId}/*` endpoints — bulk
 * generate / status / fetch / delete and per-record CRUD. The shared
 * axios client has baseURL `/api/v1`.
 */

import client from './client';

/**
 * Kick off V&V recommendation generation for a workflow.
 * Mirrors AthenaBackendService.generateVVRecommendations(threadId, analysisMode).
 *
 * Always passes `regenerate=true` to match the legacy contract — the
 * server treats this as the canonical "fresh-generate" path.
 */
export async function generateVVRecommendations(threadId, analysisMode = 'individual') {
  const params = new URLSearchParams({
    analysis_mode: analysisMode,
    regenerate: 'true',
  });
  return client.post(`/vv-recommendations/${threadId}/generate?${params.toString()}`);
}

/**
 * Poll the V&V recommendations generation status.
 * Mirrors AthenaBackendService.getVVRecommendationsStatus(threadId).
 */
export async function getVVRecommendationsStatus(threadId) {
  return client.get(`/vv-recommendations/${threadId}/status`);
}

/**
 * Fetch the V&V recommendations for a workflow.
 * Mirrors AthenaBackendService.getVVRecommendations(threadId).
 */
export async function getVVRecommendations(threadId) {
  return client.get(`/vv-recommendations/${threadId}`);
}

/**
 * Delete all V&V recommendations for a workflow.
 * Mirrors AthenaBackendService.deleteVVRecommendations(threadId).
 */
export async function deleteVVRecommendations(threadId) {
  return client.delete(`/vv-recommendations/${threadId}`);
}

/**
 * Update a single V&V recommendation record.
 * Mirrors AthenaBackendService.updateVVRecommendation(threadId, vvId, updates).
 */
export async function updateVVRecommendation(threadId, vvId, updates) {
  return client.patch(
    `/vv-recommendations/${threadId}/record/${vvId}`,
    { updates }
  );
}

/**
 * Add a new V&V recommendation record.
 * Mirrors AthenaBackendService.addVVRecommendation(threadId, newRecord).
 */
export async function addVVRecommendation(threadId, newRecord) {
  return client.post(`/vv-recommendations/${threadId}/record`, { record: newRecord });
}

/**
 * Delete a single V&V recommendation record by ID.
 * Mirrors AthenaBackendService.deleteSingleVVRecommendation(threadId, vvId).
 */
export async function deleteSingleVVRecommendation(threadId, vvId) {
  return client.delete(`/vv-recommendations/${threadId}/record/${vvId}`);
}
